import { useEffect, type ReactNode } from "react";
import { useConfetti } from "@/providers/confettiProvider";
import { useUser } from "@/hooks/useUser";
import { useNotification } from "@/hooks/useNotification";
import { useSearchParam } from "@/hooks/useSearchParam";
import { subscription } from "@/lib/auth-client";
import { cancelSubscriptionClient } from "instantdb/client/userClient";

export const ProProvider = ({ children }: { children: ReactNode }) => {
  const [subscriptionSuccess, setSubscriptionSuccess] = useSearchParam("subscriptionSuccess");
  const [subscriptionCanceled, setSubscriptionCanceled] = useSearchParam("subscriptionCanceled");

  const { showNotification } = useNotification();
  const { fireConfetti } = useConfetti();
  const { isAuthenticated, refetchUser } = useUser();

  useEffect(() => {
    const fetchSubscriptions = async () => {
      const { data: subscriptions } = await subscription.list();

      // get the active subscription
      const activeSubscription = subscriptions?.find(
        (sub) => sub.status === "active" || sub.status === "trialing",
      );

      // cancel the subscription if cancelAtPeriodEnd is true
      if (activeSubscription?.cancelAtPeriodEnd) {
        await cancelSubscriptionClient({
          userId: activeSubscription.referenceId,
        });
      }
    };

    fetchSubscriptions();
  }, []);

  // Fires confetti when subscription is successful
  useEffect(() => {
    if (isAuthenticated && subscriptionSuccess) {
      fireConfetti({ golden: true });
      setSubscriptionSuccess(undefined);
      refetchUser();

      if (window.umami) {
        window.umami.track("pro-subscription-success");
      }
    }
  }, [isAuthenticated, subscriptionSuccess, fireConfetti, setSubscriptionSuccess, refetchUser]);

  // Shows notification when checkout is incomplete
  useEffect(() => {
    if (isAuthenticated && subscriptionCanceled) {
      showNotification({
        type: "error",
        message: "Checkout Canceled",
        description: "The checkout was not completed.",
      });
      setSubscriptionCanceled(undefined);
    }
  }, [isAuthenticated, subscriptionCanceled, showNotification, setSubscriptionCanceled]);

  return children;
};
